'use strict';

import $ from 'jquery';
import mediator from '../../js/mediator';
import config from '../../js/config';
import utilities from '../../js/utilities';
import analyticsCart from '../Analytics/AnalyticsCart';
export const IMPRESSION_REQUIRED_VIEW_MILLISECONDS = 1000;
const PRODUCT_HIDDEN_DATA_KEY = 'ProductImpressionHidden';

export default class ProductImpression {

  /**
   * A central place to check whether an element
   * holds a product impression
   *
   * @param {HTMLElement} element
   * @return {*}
   */
  static isProductImpressionElement(element) {
    try {
      return !!(element && element.dataset.partId);
    } catch (err) {
      return false;
    }
  }

  /**
   * A ProductImpression instance hasn't always been
   * created when elements are manually hidden so
   * this will keep track
   *
   * @param {[HTMLElement]} elements
   */
  static setProductElementsAsHidden(elements = []) {
    elements.forEach((element) => {
      $(element).data(PRODUCT_HIDDEN_DATA_KEY, true);
    });
  }

  /**
   * A ProductImpression instance hasn't always been
   * created when elements are manually set as visible so
   * this will keep track
   *
   * @param {[HTMLElement]} elements
   */
  static setProductElementsAsVisible(elements = []) {
    elements.forEach((element) => {
      $(element).data(PRODUCT_HIDDEN_DATA_KEY, false);
    });
  }

  constructor(el, list, position, ignoreHorizontalPositioning) {
    this.el = el;
    this.$el = $(this.el);
    this.list = list;
    this.position = position;
    this.ignoreHorizontalPositioning = ignoreHorizontalPositioning;
    this.id = el.dataset.partId;
    this.name = el.dataset.sku || el.dataset.name;
    this.brand = el.dataset.manufacturer;
    this.inViewport = false;
    this.viewportTimer = false;
    this.viewed = false;
  }

  isHidden() {
    return this.$el.data(PRODUCT_HIDDEN_DATA_KEY) === true;
  }

  /**
   * Check if we are in the viewport for long enough and trigger an impression.
   *
   * @param {{height: number, width: number}} viewport
   */
  impressionCheck(viewport) {
    // Have already been viewed
    if (this.viewed) return false;

    // Has a valid part id
    if (!this.id) return false;

    let wasProductInViewport = this.inViewport;
    this.inViewport = utilities.isElementCompletelyInViewport(this.el, null, viewport, this.ignoreHorizontalPositioning)
      && !this.isHidden();

    // If we started tracking the product but no longer are
    if (wasProductInViewport && !this.inViewport) {
      clearTimeout(this.viewportTimer);
    }

    // We are now in the viewport and were not previously
    if (this.inViewport && !wasProductInViewport) {
      this.viewportTimer = setTimeout(() => {
        mediator.trigger(config.events.productImpressions.viewed, this);
      }, IMPRESSION_REQUIRED_VIEW_MILLISECONDS);
    }
  }

  /**
   * Get the product image
   * @returns {string|undefined}
   * @private
   */
  _getImageUrl() {
    let imageUrl = this.$el.find('.InformationTile-details-productImage, .SearchResults-image').attr('src');

    // Return just the filename without the path
    return imageUrl ? imageUrl.replace(/.*?\/([^\/]+)$/, '$1') : imageUrl;
  }

  _getSourcePartAndRegion() {
    let sourcePartId = this.el.dataset.sourcePartId;
    let sourceCode = this.el.dataset.inventoryRegion || this.el.dataset.sourceCode;

    if (sourcePartId && sourceCode) {
      return `${sourcePartId} / ${sourceCode}`;
    } else if (sourcePartId && !sourceCode) {
      return `${sourcePartId}`;
    } else if (!sourcePartId && sourceCode) {
      return `${sourceCode}`;
    }

    return undefined;
  }

  _getUnitOfMeasure() {
    let unitOfMeasure = this.el.dataset.unitOfMeasure;

    if (unitOfMeasure) {
      return unitOfMeasure;
    }

    return (unitOfMeasure === '') ? 'ea' : undefined;
  }
  _getdays() {
    let daysUntilDispatch = this.el.dataset.daysUntilDispatch;

    if (daysUntilDispatch) {
      return daysUntilDispatch;
    }

    return (daysUntilDispatch === '') ? '' : daysUntilDispatch;
  }
  _getManufacturerLeadTime() {
    let manufacturerLeadTime = this.el.dataset.manufacturerLeadTime;

    if (manufacturerLeadTime) {
      return manufacturerLeadTime;
    }

    return (manufacturerLeadTime === '') ? '' : manufacturerLeadTime;
  }
  _getShipsFrom() {
    let shipsFrom = this.el.dataset.countryCodeName;

    if (shipsFrom) {
      return shipsFrom;
    }

    return (shipsFrom === '') ? '' : shipsFrom;
  }
  _getStockExists() {
    let stock = this.el.dataset.quantity;

    return (stock && stock > 0) ? true : false;
  }

  /**
   * Get the impression data required for the datalayer.
   *
   * @returns {object}
   */
  getDatalayerImpression() {
    let dataSet = this.el.dataset;
    let impressions = {
      id: this.id,
      name: this.name,
      price: dataSet.price || 0,
      brand: this.brand ? this.brand.toUpperCase() : '',
      category: dataSet.category ? dataSet.category : '',
      variant: dataSet.variant,
      position: this.position,
      dimension27: this._getManufacturerLeadTime(),
      dimension34: this._getdays(),
      dimension35: this._getShipsFrom(),
      dimension12: this._getSourcePartAndRegion(),
      dimension13: this._getImageUrl(),
      dimension14: dataSet.quantity,
      dimension17: dataSet.buyingOptionsCount,
      dimension25: this._getStockExists(),
      dimension29: dataSet.packagingTypes || 'Part',
      // dimension35: dataSet.shipsFromCountryName,
      dimension36: dataSet.eccnCode,
      dimension37: dataSet.isRohsCompliant && dataSet.isRohsCompliant.toString().toLowerCase(),
      dimension41: dataSet.buyingOptionsCount,
      dimension42: dataSet.buyingOptionsCount,
      dimension43: dataSet.buyingOptionsCount,
      dimension44: this._getUnitOfMeasure(),
      dimension51: dataSet.hint,
      dimension53: dataSet.npi,
      dimension58: dataSet.isDiscontinued,
      dimension78: dataSet.hasTieredPricing
    };
    let validPromoGroup = analyticsCart.validatePromoGroup(dataSet.promoGroup);
    if (validPromoGroup) {
      impressions.dimension67 = dataSet.promoGroup;
    }

    impressions.list = this.list;
    return impressions;
  }

  /**
   * Check to see if we have pricing information available or not.
   *
   * @returns {boolean}
   */
  waitingForPricing() {
    return $(this.el).is('.ProductPricing, .SearchResultsProductPricing');
  }

  toString() {
    return `${this.list}[${this.position}].${this.name}`;
  }
}



// WEBPACK FOOTER //
// ./assets/components/ProductImpressions/ProductImpression.js